import { useEffect, useState, type MouseEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import Logo from "@/components/base/Logo";
import SolucoesMegaMenu from "@/components/feature/SolucoesMegaMenu";
import BlogMegaMenu from "@/components/feature/BlogMegaMenu";
import { whatsappLink } from "@/constants/site";

interface NavItem {
  label: string;
  to: string;
  highlight?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { label: "Início", to: "/" },
  { label: "Hospedagem", to: "/hospedagem" },
  { label: "VPS", to: "/vps" },
  { label: "Servidores", to: "/servidores" },
  { label: "Revenda", to: "/revenda" },
  { label: "Domínios", to: "/dominios" },
  { label: "Criação de Sites", to: "/sites" },
  { label: "Sobre", to: "/sobre" },
  { label: "Suporte", to: "/suporte" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const solid = scrolled || open;

  const handleNavClick = (item: NavItem) => (event: MouseEvent<HTMLAnchorElement>) => {
    setOpen(false);
    const hashIndex = item.to.indexOf("#");
    if (hashIndex === -1) return;
    const path = item.to.slice(0, hashIndex) || "/";
    const hash = item.to.slice(hashIndex + 1);
    if (location.pathname !== path) return;
    const el = document.getElementById(hash);
    if (!el) return;
    event.preventDefault();
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    window.history.replaceState(null, "", `${path}${window.location.search}#${hash}`);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        solid
          ? "bg-background-50/90 backdrop-blur-md border-b border-background-200"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 md:h-20 items-center justify-between gap-3 px-4 md:px-8">
        <Logo tone={solid ? "dark" : "light"} />

        <nav className="hidden xl:flex items-center gap-0.5">
          <Link
            to={NAV_ITEMS[0].to}
            onClick={handleNavClick(NAV_ITEMS[0])}
            className={`inline-flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              solid
                ? "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                : "text-background-100 hover:text-background-50 hover:bg-background-50/10"
            }`}
          >
            {NAV_ITEMS[0].label}
          </Link>

          <SolucoesMegaMenu solid={solid} />

          <BlogMegaMenu solid={solid} />

          {NAV_ITEMS.slice(1).map((item) => (
            <Link
              key={item.label}
              to={item.to}
              onClick={handleNavClick(item)}
              className={`inline-flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                solid
                  ? "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                  : "text-background-100 hover:text-background-50 hover:bg-background-50/10"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/area-do-cliente"
            onClick={() => setOpen(false)}
            className={`hidden md:inline-flex items-center gap-2 whitespace-nowrap rounded-md border px-3.5 py-2.5 text-xs font-semibold uppercase tracking-wide transition-colors ${
              solid
                ? "border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                : "border-background-50/25 text-background-50 hover:bg-background-50/10"
            }`}
          >
            <i className="ri-user-3-line text-base" aria-hidden="true"></i>
            Área do Cliente
          </Link>

          <a
            href={whatsappLink()}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden xl:inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-customer-service-2-line text-base" aria-hidden="true"></i>
            Falar com um especialista
          </a>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label="Abrir menu"
            aria-expanded={open}
            className={`xl:hidden flex h-10 w-10 items-center justify-center rounded-md transition-colors ${
              solid ? "text-foreground-950 hover:bg-background-100" : "text-background-50 hover:bg-background-50/10"
            }`}
          >
            <i className={open ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"} aria-hidden="true"></i>
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden bg-background-50 border-t border-background-200 px-4 pb-6 pt-2">
          <nav className="flex flex-col">
            <Link
              to={NAV_ITEMS[0].to}
              onClick={handleNavClick(NAV_ITEMS[0])}
              className="inline-flex items-center gap-2 rounded-md px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 hover:text-primary-600"
            >
              {NAV_ITEMS[0].label}
            </Link>

            <Link
              to="/solucoes"
              onClick={() => setOpen(false)}
              className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-3 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600"
            >
              <i className="ri-sparkling-2-line text-base" aria-hidden="true"></i>
              Soluções
            </Link>

            <Link
              to="/blog"
              onClick={() => setOpen(false)}
              className="inline-flex items-center gap-2 rounded-md px-3 py-3 text-sm font-semibold text-foreground-800 hover:bg-background-100 hover:text-primary-600"
            >
              <i className="ri-article-line text-base" aria-hidden="true"></i>
              Blog
            </Link>

            {NAV_ITEMS.slice(1).map((item) => (
              <Link
                key={item.label}
                to={item.to}
                onClick={handleNavClick(item)}
                className="inline-flex items-center gap-2 rounded-md px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 hover:text-primary-600"
              >
                {item.label}
              </Link>
            ))}

            <Link
              to="/area-do-cliente"
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex items-center gap-2 rounded-md border border-background-300 px-3 py-3 text-sm font-semibold text-foreground-800 hover:border-primary-300 hover:text-primary-600"
            >
              <i className="ri-user-3-line text-lg" aria-hidden="true"></i>
              Área do Cliente
            </Link>

            <a
              href={whatsappLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600"
            >
              <i className="ri-whatsapp-line text-lg" aria-hidden="true"></i>
              Falar com um especialista
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}